import clsx from "clsx"
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { Icon } from "@/common/icons/Icon"
import { type IconName } from "@/common/icons/Icon.types"

export type ToastType = "success" | "error" | "warning" | "info"

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastIconAction {
  icon: IconName
  onClick: () => void
  ariaLabel: string
}

export interface ToastOptions {
  autoClose?: number | false
  description?: string
  action?: ToastAction
  iconActions?: ToastIconAction[]
  stackId?: string
  position?:
    | "top-right"
    | "top-left"
    | "bottom-right"
    | "bottom-left"
    | "top-center"
    | "bottom-center"
}

export interface Toast {
  id: string
  type: ToastType
  message: string
  options: ToastOptions
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (type: ToastType, message: string, options?: ToastOptions) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

const defaultOptions: ToastOptions = {
  autoClose: 3000,
  position: "top-right"
}

const toastIconConfig: Record<
  ToastType,
  { name: "circle-check" | "circle-x" | "triangle-exclamation" | "circle-info"; color: string }
> = {
  success: { name: "circle-check", color: "text-green-600" },
  error: { name: "circle-x", color: "text-red-600" },
  warning: { name: "triangle-exclamation", color: "text-yellow-500" },
  info: { name: "circle-info", color: "text-blue-600" }
}

// Single Toast Item
const ToastItem: React.FC<{
  toast: Toast
  onClose: (id: string) => void
  skipEntrance?: boolean
}> = ({ toast, onClose, skipEntrance = false }) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const remainingTimeRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const [isExiting, setIsExiting] = useState(false)
  const [isMounted, setIsMounted] = useState(skipEntrance)

  const { name: iconName, color: iconColor } = toastIconConfig[toast.type]

  const startAutoClose = useCallback(() => {
    if (toast.options.autoClose === false) return

    const autoCloseTime =
      typeof toast.options.autoClose === "number" ? toast.options.autoClose : 3000

    const timeToUse = remainingTimeRef.current ?? autoCloseTime
    remainingTimeRef.current = null

    startTimeRef.current = Date.now()

    timeoutRef.current = setTimeout(() => {
      setIsExiting(true)
      setTimeout(() => {
        onClose(toast.id)
      }, 100)
    }, timeToUse)
  }, [toast.id, toast.options.autoClose, onClose])

  const pauseAutoClose = useCallback(() => {
    if (timeoutRef.current && startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current
      const autoCloseTime =
        typeof toast.options.autoClose === "number" ? toast.options.autoClose : 3000
      remainingTimeRef.current = Math.max(0, autoCloseTime - elapsed)
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [toast.options.autoClose])

  const resumeAutoClose = useCallback(() => {
    if (remainingTimeRef.current !== null && remainingTimeRef.current > 0) {
      startAutoClose()
    }
  }, [startAutoClose])

  useEffect(() => {
    setIsMounted(true)
    startAutoClose()
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [startAutoClose])

  const handleClose = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => {
      onClose(toast.id)
    }, 100)
  }, [toast.id, onClose])

  const position = toast.options.position || "top-right"
  const isRightSide = position.includes("right")
  const isLeftSide = position.includes("left")
  const isCenter = position.includes("center")

  const getAnimationClasses = () => {
    if (isExiting) {
      if (skipEntrance) return "scale-95 opacity-0"
      if (isRightSide) return "translate-x-full opacity-0"
      if (isLeftSide) return "-translate-x-full opacity-0"
      if (isCenter) return "translate-y-2 opacity-0"
      return "translate-x-full opacity-0"
    }
    if (isMounted) {
      return "translate-x-0 translate-y-0 scale-100 opacity-100"
    }
    if (isRightSide) return "translate-x-full opacity-0"
    if (isLeftSide) return "-translate-x-full opacity-0"
    if (isCenter) return "translate-y-2 opacity-0"
    return "translate-x-full opacity-0"
  }

  return (
    <div
      className={clsx(
        "rounded-nx-lg bg-nx-surface-bg-base pointer-events-auto w-[400px] border border-zinc-200 shadow-none",
        "transform transition-all duration-100 ease-in-out",
        getAnimationClasses()
      )}
      role="alert"
      onMouseEnter={pauseAutoClose}
      onMouseLeave={resumeAutoClose}
    >
      <div
        className={clsx(
          "flex gap-2.5 px-3 py-3",
          toast.options.description || toast.options.action || toast.options.iconActions
            ? "items-start"
            : "items-center"
        )}
      >
        <div className="flex h-5 shrink-0 items-center">
          <Icon name={iconName} size={16} className={iconColor} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm leading-5 font-semibold text-zinc-900">{toast.message}</p>
          {toast.options.description && (
            <p className="mt-0.5 text-sm leading-5 text-zinc-500">{toast.options.description}</p>
          )}
        </div>

        <div className="flex h-5 shrink-0 items-center">
          <button
            onClick={handleClose}
            className="rounded-nx-full p-0.5 text-zinc-400 transition-colors duration-150 hover:bg-zinc-100 hover:text-zinc-600"
            aria-label="Close"
          >
            <Icon name="cross-small" size={16} />
          </button>
        </div>
      </div>

      {(toast.options.action || toast.options.iconActions) && (
        <div className="border-nx-stroke-default bg-nx-surface-bg-tertiary rounded-b-nx-lg flex items-center justify-between border-t px-4 py-2">
          <div>
            {toast.options.action && (
              <button
                onClick={toast.options.action.onClick}
                className="text-nx-text-secondary hover:text-nx-text-label text-sm font-medium hover:underline"
              >
                {toast.options.action.label}
              </button>
            )}
          </div>

          {toast.options.iconActions && (
            <div className="flex items-center gap-1">
              {toast.options.iconActions.map((iconAction) => (
                <button
                  key={`${iconAction.icon}-${iconAction.ariaLabel}`}
                  onClick={iconAction.onClick}
                  className="rounded-nx-full shrink-0 p-px text-zinc-400 transition-colors duration-150 hover:bg-zinc-100 hover:text-zinc-600"
                  aria-label={iconAction.ariaLabel}
                >
                  <Icon name={iconAction.icon} size={14} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Stacked Toast Group
const STACK_OFFSET = 4

const ToastStack: React.FC<{ toasts: Toast[]; onClose: (id: string) => void }> = ({
  toasts,
  onClose
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const stackCount = toasts.length
  const maxStrips = Math.min(stackCount - 1, 2)
  const topToast = toasts[toasts.length - 1]
  const hiddenToasts = toasts.slice(0, -1)

  const handleMouseEnter = useCallback(() => {
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current)
      collapseTimeoutRef.current = null
    }
    setIsExpanded(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    collapseTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false)
    }, 300)
  }, [])

  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current)
      }
    }
  }, [])

  // ToastStack only renders for non-empty stacks, but guard so `topToast` is
  // provably defined for the render below.
  if (!topToast) return null

  return (
    <div className="relative">
      {/* Hover target — expands/collapses the stack */}
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {/* Hidden toasts — revealed on hover */}
        <div
          className="flex flex-col gap-2 overflow-hidden transition-all duration-200 ease-out"
          style={{
            maxHeight: isExpanded ? `${hiddenToasts.length * 200}px` : "0px",
            opacity: isExpanded ? 1 : 0,
            marginBottom: isExpanded ? 8 : 0
          }}
        >
          {hiddenToasts.map((t) => (
            <ToastItem key={t.id} toast={t} onClose={onClose} skipEntrance />
          ))}
        </div>

        {/* Top toast + decorative stack strips */}
        <div className="relative">
          {/* Decorative strips — only when collapsed */}
          {Array.from({ length: maxStrips }).map((_, i) => {
            const depth = i + 1
            return (
              <div
                key={`strip-${depth}`}
                className="rounded-nx-lg bg-nx-surface-bg-base pointer-events-none absolute inset-x-0 top-0 h-full border border-zinc-200 transition-all duration-200 ease-out"
                style={{
                  transform: isExpanded
                    ? "translateY(0) scale(1)"
                    : `translateY(${depth * STACK_OFFSET}px) scale(${1 - depth * 0.03})`,
                  opacity: isExpanded ? 0 : 1 - depth * 0.15,
                  zIndex: -depth
                }}
              />
            )
          })}

          {/* Top toast */}
          <div className="relative z-10">
            <ToastItem key={topToast.id} toast={topToast} onClose={onClose} skipEntrance />
          </div>
        </div>
      </div>

      {/* Count badge / dismiss all — outside hover target */}
      {stackCount > 1 && (
        <div
          className="group rounded-nx-full pointer-events-auto absolute -top-2 -right-2 z-20 flex h-5 w-5 cursor-pointer items-center justify-center bg-zinc-900 transition-all duration-200 hover:bg-red-600"
          style={{ opacity: isExpanded ? 0 : 1, pointerEvents: isExpanded ? "none" : "auto" }}
          onClick={() => toasts.forEach((t) => onClose(t.id))}
        >
          <span className="block text-[11px] font-semibold text-white group-hover:hidden">
            {stackCount}
          </span>
          <Icon name="cross-small" size={12} className="hidden text-white group-hover:block" />
        </div>
      )}
    </div>
  )
}

// Toast Container
const ToastContainer: React.FC<{ toasts: Toast[]; onClose: (id: string) => void }> = ({
  toasts,
  onClose
}) => {
  if (typeof window === "undefined") return null

  // Group by position, then by type within each position
  const toastsByPosition = toasts.reduce(
    (acc, toast) => {
      const position = toast.options.position || "top-right"
      let positionToasts = acc[position]
      if (!positionToasts) {
        positionToasts = []
        acc[position] = positionToasts
      }
      positionToasts.push(toast)
      return acc
    },
    {} as Record<string, Toast[]>
  )

  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "top-center": "top-4 left-1/2 -translate-x-1/2",
    "bottom-center": "bottom-4 left-1/2 -translate-x-1/2"
  }

  return createPortal(
    <>
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => {
        // Group toasts by stackId, ungrouped toasts get their own entry
        const stacks: { key: string; toasts: Toast[] }[] = []
        const stackMap: Record<string, Toast[]> = {}

        for (const t of positionToasts) {
          const sid = t.options.stackId
          if (sid) {
            if (!stackMap[sid]) {
              stackMap[sid] = []
              stacks.push({ key: `stack-${sid}`, toasts: stackMap[sid] })
            }
            stackMap[sid].push(t)
          } else {
            stacks.push({ key: t.id, toasts: [t] })
          }
        }

        return (
          <div
            key={position}
            className={clsx(
              "pointer-events-none fixed z-[9999] flex flex-col gap-3",
              positionClasses[position as keyof typeof positionClasses]
            )}
          >
            {stacks.map(({ key: stackKey, toasts: stackToasts }) => {
              const [singleToast] = stackToasts
              return stackToasts.length === 1 && singleToast ? (
                <ToastItem key={stackKey} toast={singleToast} onClose={onClose} />
              ) : (
                <ToastStack key={stackKey} toasts={stackToasts} onClose={onClose} />
              )
            })}
          </div>
        )
      })}
    </>,
    document.body
  )
}

// Toast Provider
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const addToast = useCallback((type: ToastType, message: string, options?: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const mergedOptions = { ...defaultOptions, ...options }

    const newToast: Toast = {
      id,
      type,
      message,
      options: mergedOptions
    }

    setToasts((prev) => [...prev, newToast])
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  )
}

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider")
  }
  return context
}

// Utility object for imperative API (similar to react-toastify)
let globalAddToast: ((type: ToastType, message: string, options?: ToastOptions) => void) | null =
  null

export const _setGlobalToastFunction = (
  addToastFn: (type: ToastType, message: string, options?: ToastOptions) => void
) => {
  globalAddToast = addToastFn
}

export const toast = {
  success: (message: string, options?: ToastOptions) => {
    if (globalAddToast) {
      globalAddToast("success", message, options)
    } else {
      console.warn("Toast provider not initialized")
    }
  },
  error: (message: string, options?: ToastOptions) => {
    if (globalAddToast) {
      globalAddToast("error", message, options)
    } else {
      console.warn("Toast provider not initialized")
    }
  },
  warning: (message: string, options?: ToastOptions) => {
    if (globalAddToast) {
      globalAddToast("warning", message, options)
    } else {
      console.warn("Toast provider not initialized")
    }
  },
  info: (message: string, options?: ToastOptions) => {
    if (globalAddToast) {
      globalAddToast("info", message, options)
    } else {
      console.warn("Toast provider not initialized")
    }
  }
}

// HOC to inject global toast function
export const ToastProviderWithGlobal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { addToast } = useToast()

  React.useEffect(() => {
    _setGlobalToastFunction(addToast)
    return () => {
      globalAddToast = null
    }
  }, [addToast])

  return <>{children}</>
}

// Combined provider for convenience
export const ToastProviderComplete: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ToastProvider>
      <ToastProviderWithGlobal>{children}</ToastProviderWithGlobal>
    </ToastProvider>
  )
}
